import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './pages/Login';
import MainLayout from './layouts/MainLayout';
import PageRouter from './layouts/PageRouter';
import MobileMain from './pages/mobile/MobileMain'; // 🚀 모바일 메인 레이아웃 유지
import { MENUS } from './config/menuConfig';
import { userService } from './services/userService';
import { logService } from './services/logService';
import { setCurrentRole } from './utils/permissions';

// 역할에 따른 초기 진입 경로
const initialPathForRole = (role) => (role === 'RECALL' ? 'recall' : 'dashboard');

function App() {
  const [user, setUser] = useState(null);
  // 🚀 [보안] 인증은 Firebase가 관리 → 앱 시작 시 로그인 상태 복원까지 잠깐 로딩
  const [authLoading, setAuthLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(
    localStorage.getItem('E2B_CURRENT_PATH') || 'dashboard'
  );

  // 모바일 기기 접속 여부 감지 상태 유지 (768px 이하)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // 🚀 [핵심] Firebase 로그인 상태를 구독하여, 새로고침/재접속 시에도 세션을 안전하게 복원
  useEffect(() => {
    const unsubscribe = userService.subscribeAuth(async (firebaseUser) => {
      if (firebaseUser) {
        const profile = await userService.getProfile(firebaseUser);
        // 프로필이 없거나 승인 대기 상태면 즉시 로그아웃 처리
        if (!profile || profile.status !== 'APPROVED') {
          await userService.logout();
          logService.setOperator(''); setCurrentRole('');
          setUser(null);
        } else {
          // 새로고침으로 세션이 복원된 경우에도 감사 로그의 작업자 이름이 유지되도록 지정
          logService.setOperator(profile.name); setCurrentRole(profile.role);
          setUser(profile);
        }
      } else {
        logService.setOperator(''); setCurrentRole('');
        setUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 실시간 화면 크기 감지 이벤트 리스너 유지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogin = (userInfo) => {
    // 로그인은 이미 Login 화면에서 Firebase 인증까지 끝난 상태 → 화면 전환만 처리
    setUser(userInfo);
    setCurrentRole(userInfo.role);
    const startPath = initialPathForRole(userInfo.role);
    setCurrentPath(startPath);
    localStorage.setItem('E2B_CURRENT_PATH', startPath);
  };

  const handleLogout = async () => {
    await userService.logout(); // Firebase 세션 종료 → 구독 콜백이 user를 null로 정리
    localStorage.removeItem('E2B_CURRENT_PATH');
    localStorage.removeItem('E2B_MOBILE_CACHE_V1'); // 로그아웃 시 기기에 남은 고객 데이터 캐시 제거
    setUser(null);
    setCurrentPath('dashboard');
  };

  const handleNavigate = (path, param = null) => {
    if (path === 'part' && param) {
      sessionStorage.setItem('E2B_PART_SEARCH_PARAM', param);
    }
    setCurrentPath(path);
    localStorage.setItem('E2B_CURRENT_PATH', path);
  };

  // 0. 로그인 상태 복원 중: 잠깐 로딩 화면 (로그인 화면 깜빡임 방지)
  if (authLoading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
        background: '#f8fafc', color: '#64748b', fontSize: '14px', fontWeight: 'bold',
      }}>
        불러오는 중...
      </div>
    );
  }

  // 1. 미인증 상태인 경우 하이브리드 로그인 화면 노출
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // 권한별 메뉴 필터링 유지
  const filteredMenus = MENUS.filter(menu => menu.roles.includes(user.role));

  // 2. 인증 성공 후 모바일 접속인 경우: 모바일 전용 뷰 허브 작동
  if (isMobile) {
    return (
      <MobileMain
        user={user}
        onLogout={handleLogout}
      />
    );
  }

  // 3. 인증 성공 후 PC 접속인 경우: 기존 정밀 레이아웃 유지
  return (
    <MainLayout
      user={user}
      menus={filteredMenus}
      onLogout={handleLogout}
      onMenuClick={handleNavigate}
    >
      <PageRouter
        currentPath={currentPath}
        user={user}
        onMenuClick={handleNavigate}
      />
    </MainLayout>
  );
}

export default App;
